import { signal } from '@preact/signals';
/**
 * The before/after lens (BENCH-V2-SPEC §13.1 to §13.3): a side panel beside the reader, not woven into `VirtualReader`'s
 * virtualised grid. Per channel the document-scale score before and after (Tier C); per passage its status, the kept-best
 * mark with "Revert to original", the diff, the dialogue-guard notes; the ledger's reverted passages; the document-wide goals;
 * and the coverage counts.
 */
import type { FailureReport, SEVERITIES } from '../../engine/bench/failure-report.ts';
import type { UnitLocal } from '../../engine/bench/inventory.ts';
import type { Lens2, UnitLens } from '../../engine/bench/lens2.ts';
import { ChannelTerm, Term } from '../components/Term.tsx';
import { fmt1, fmtInt, fmtSigned1 } from '../format.ts';
import { addNotice, result } from '../state.ts';
import { LENS, OVERVIEW, REPORT_EXPORT, VERSIONS } from '../strings-bench.ts';
import { BENCH_EXPLAIN } from '../strings-explain.ts';
import { ChannelIdsIn, TermIn } from './TermIn.tsx';
import {
	revertAllKept,
	revertAllLenient,
	revertBySeverity,
	revertChunk,
	revertUnits,
} from './controller.ts';
import {
	exportAppendix,
	jobs,
	lastReport,
	lens,
	lensEditable,
	lensJobId,
	lensOpen,
	overview,
	overviewOpen,
	revertedUnits,
	severityFilter,
} from './state.ts';

/** The failure report of a kept or failed passage (BENCH-V3-PLAN §7.2): severity, the targeted outcome, the signal, the rises. */
function ReportView({ r }: { r: FailureReport }) {
	const R = LENS.report;
	return (
		<div class="lens-report" data-testid="lens-report" data-severity={r.severity}>
			<p>
				<strong>{R.title}</strong> ·{' '}
				<span title={LENS.severityHint[r.severity]}>{R.severity(LENS.severities[r.severity])}</span>
			</p>
			<p>{r.summary}</p>
			<ul class="dim">
				{r.targeted.map((t) => (
					<li key={t.channel} class="mono">
						{R.targeted(
							t.channel,
							t.status,
							t.gone === undefined ? '' : String(t.gone),
							t.of === undefined ? '' : String(t.of)
						)}
					</li>
				))}
				{r.signal ? (
					<li>
						{r.signal.S !== undefined && r.signal.R !== undefined
							? R.signal(
									r.signal.S.toFixed(2),
									r.signal.R.toFixed(2),
									(r.signal.I ?? 0).toFixed(2),
									(r.signal.B ?? 0).toFixed(2)
								)
							: R.net((r.signal.net ?? 0).toFixed(2))}
					</li>
				) : null}
				{r.document.map((d) => (
					<li key={`d-${d.channel}`} class="mono">
						{R.document(d.channel, d.points.toFixed(2), d.margin.toFixed(2), d.phrase ?? '')}
					</li>
				))}
				{r.window.map((w) => (
					<li key={`w-${w.channel}-${w.para}`} class="mono">
						{R.window(w.channel, String(w.para), w.cross === true)}
						{w.spans.length > 0 ? ` ${w.spans.map((x) => `“${x}”`).join(', ')}` : ''}
					</li>
				))}
				{r.introduced.map((i) => (
					<li key={`i-${i.channel}-${i.lost ? 'l' : 'n'}`} class="mono">
						{R.introduced(i.channel, i.spans.map((x) => `“${x}”`).join(', '), i.lost === true)}
					</li>
				))}
				{r.checks.map((c) => (
					<li key={c}>{c}</li>
				))}
			</ul>
		</div>
	);
}

export function CoveragePanel({ l }: { l: Lens2 }) {
	return (
		<section class="coverage" data-testid="coverage-panel" aria-label={LENS.coverageTitle}>
			<h3>{LENS.coverageTitle}</h3>
			<p data-testid="coverage-tiled">
				{LENS.coverage(String(l.coverage.records), String(l.coverage.chunks))}
			</p>
			<table class="coverage-table">
				<tbody>
					{l.coverage.byChannel.map((c) => (
						<tr key={c.channel} data-channel={c.channel}>
							<th scope="row" class="mono">
								{c.channel}
							</th>
							<td>
								{Object.entries(c.counts)
									.map(([state, n]) => `${n} ${state}`)
									.join(', ')}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			{l.goals.length > 0 ? (
				<>
					<h4>{LENS.goalsTitle}</h4>
					<ul data-testid="coverage-goals">
						{l.goals.map((g) => (
							<li key={g.id} class="mono">
								{LENS.goal(g.channel, fmt1(g.baseline), fmt1(g.target), fmt1(g.achieved))}
							</li>
						))}
					</ul>
				</>
			) : null}
		</section>
	);
}

/** The segment's paragraphs as the reader numbers them: `¶a` for one paragraph, `¶a–¶b` for several (1-based). */
function localSpan(x: UnitLocal): string {
	const a = x.paraStart + 1;
	return x.paraEnd - x.paraStart > 1 ? `¶${a}–¶${x.paraEnd}` : `¶${a}`;
}

/** The localised figure of each targeted channel (UD-45, UD-46): the segment, its score and data weight, and its flag. */
function LocalFigure({ local }: { local: readonly UnitLocal[] }) {
	return (
		<p class="dim" data-testid="lens-local">
			<Term k="segment">{LENS.localTitle}</Term>:{' '}
			{local.map((x, i) => (
				<span key={x.channel}>
					{i > 0 ? '; ' : ''}
					<ChannelTerm id={x.channel} />{' '}
					{x.source === 'document' ? (
						LENS.localDocument
					) : (
						<>
							{LENS.localSegment(localSpan(x), fmtInt(x.words), fmt1(x.score), x.kappa.toFixed(2))}
							{', '}
							{x.hotspot ? (
								<Term k="hotspot">{LENS.localFlags.hotspot}</Term>
							) : x.elevated ? (
								<Term k="elevated">{LENS.localFlags.elevated}</Term>
							) : (
								LENS.localFlags.none
							)}
						</>
					)}
				</span>
			))}
		</p>
	);
}

function UnitRow({ u, reverted }: { u: UnitLens; reverted: boolean }) {
	const mark = reverted ? 'reverted' : u.mark;
	return (
		<li key={u.id} data-status={mark} data-testid={`lens-unit-${u.id}`}>
			<strong>{LENS.marks[mark]}</strong>{' '}
			<span class="mono dim">
				{u.channels.map((c, i) => (
					<span key={c}>
						{i > 0 ? ', ' : ''}
						<ChannelTerm id={c} />
					</span>
				))}
			</span>
			{u.keptBest && !reverted ? (
				<p
					class="kept-best-mark"
					data-testid={u.keptLenient ? 'kept-lenient-mark' : 'kept-best-mark'}
				>
					{u.keptLenient ? LENS.keptLenientMark : LENS.keptBestMark}
				</p>
			) : null}
			{u.report && (u.keptBest || u.mark === 'failed') ? <ReportView r={u.report} /> : null}
			{u.version && (u.keptBest || u.mark === 'failed') ? (
				<p class="dim" data-testid="lens-version">
					{LENS.version(
						u.version.strategy,
						u.version.others
							.map((o) => LENS.versionNote(o.strategy, o.outcome, o.reason ?? ''))
							.join('; ')
					)}
				</p>
			) : null}
			{u.messages.map((m) => (
				<p key={m} class="dim">
					<ChannelIdsIn text={m} />
				</p>
			))}
			{u.guard.length > 0 ? (
				<p class="dim" data-testid="lens-guard">
					<Term k="dialogue-checks">{LENS.guardTitle}</Term>: {u.guard.join(' ')}
				</p>
			) : null}
			{u.review && u.review.length > 0 ? (
				<p class="dim" data-testid="lens-review">
					<Term k="meaning-review">{LENS.reviewTitle}</Term>: {u.review.join(' ')}
				</p>
			) : null}
			{u.signal ? (
				<p class="dim" data-testid="lens-signal">
					<Term k="signal-score">{LENS.signalTitle}</Term>:{' '}
					{LENS.signal(
						u.signal.rule,
						u.signal.S.toFixed(2),
						u.signal.R.toFixed(2),
						u.signal.I.toFixed(2),
						u.signal.B.toFixed(2)
					)}
					{u.signal.channels.length > 0 ? (
						<span class="mono">
							{' '}
							{u.signal.channels.map((c, i) => (
								<span key={c.channel}>
									{i > 0 ? ', ' : ''}
									<ChannelTerm id={c.channel} />{' '}
									{`${c.points < 0 ? '−' : '+'}${Math.abs(c.points).toFixed(2)}`}
								</span>
							))}
						</span>
					) : null}
				</p>
			) : null}
			{u.local && u.local.length > 0 ? <LocalFigure local={u.local} /> : null}
			{reverted ? <p class="dim">{LENS.revertedNote}</p> : null}
			{u.declined !== undefined ? (
				// the editor left the passage as it is (GR2): the reason stands in place of a diff
				<p data-testid="declined-reason">{LENS.declinedReason(u.declined)}</p>
			) : null}
			{u.diff
				.filter((d) => d.kind !== 'same')
				.map((d, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: a diff hunk has no stable id
					<p key={i} class={`diff-${d.kind}`}>
						{d.ops.map((op, j) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: an op has no stable id
							<span key={j} class={`diff-op-${op.kind}`}>
								{op.text}
							</span>
						))}
					</p>
				))}
			{u.why && !reverted ? (
				// the editor's one-sentence rationale for the rewrite (GR2)
				<p class="dim" data-testid="unit-why">
					{LENS.why(u.why)}
				</p>
			) : null}
			{u.reviewNote && !reverted ? (
				// the meaning review's note on the rationale (GR3B): shown, never a failure
				<p class="dim" data-testid="unit-review-note">
					<Term k="meaning-review">{LENS.reviewNoteTitle}</Term>: {LENS.reviewNote(u.reviewNote)}
				</p>
			) : null}
			{u.mark !== 'not-selected' && u.diff.length > 0 && !reverted ? (
				<button
					type="button"
					class="btn btn-secondary btn-sm"
					data-testid="lens-revert-unit"
					disabled={!lensEditable.value}
					onClick={() => revertUnits([u.id])}
				>
					{LENS.revertUnit}
				</button>
			) : null}
		</li>
	);
}

/** The rewrite report is being made (one download at a time). */
const reportBuilding = signal(false);

/** Downloads the report of the run the lens shows; a failure is a notice. */
async function downloadReport(vm: NonNullable<typeof result.value>): Promise<void> {
	reportBuilding.value = true;
	try {
		// the download module is loaded on first use: it and the builder stay out of the initial bundle (CD-131)
		const { downloadRewriteReport } = await import('./report-export.ts');
		await downloadRewriteReport(vm);
	} catch (e) {
		addNotice(REPORT_EXPORT.failed((e as Error).message));
	} finally {
		reportBuilding.value = false;
	}
}

export function LensPanel() {
	const l = lens.value;
	const vm = result.value;
	const gone = revertedUnits.value;
	const kept = l?.units.filter((u) => u.keptBest && !gone.has(u.id)) ?? [];
	const filter = severityFilter.value;
	const shown =
		l?.units.filter(
			(u) =>
				(u.mark !== 'not-selected' || u.diff.length > 0) &&
				(filter === 'all' || u.report?.severity === filter)
		) ?? [];
	const keptLenient = l?.units.filter((u) => u.keptLenient && !gone.has(u.id)) ?? [];
	// the run the lens shows: two runs of one document are told apart by the editorial guardrails (ED-191)
	const lensJob = jobs.value.find((j) => j.id === lensJobId.value);
	return (
		<aside class="lens" data-testid="lens-panel" aria-label={LENS.title}>
			<div class="lens-head">
				<h2>{LENS.title}</h2>
				{l && lastReport.value && vm ? (
					// the report of this run as a Markdown file: where each passage is, why it was rewritten, the human ranges
					<button
						type="button"
						class="btn btn-secondary"
						data-testid="lens-report-download"
						title={REPORT_EXPORT.lensButtonHelp}
						disabled={reportBuilding.value}
						onClick={() => void downloadReport(vm)}
					>
						{REPORT_EXPORT.lensButton}
					</button>
				) : null}
				{overview.value && overview.value.job.docHash === l?.docHash ? (
					// the job's before/after overview (UD-22), reopened over the lens
					<button
						type="button"
						class="btn btn-secondary"
						data-testid="lens-overview"
						onClick={() => {
							overviewOpen.value = true;
						}}
					>
						{OVERVIEW.reopen}
					</button>
				) : null}
				<button
					type="button"
					class="btn btn-secondary"
					onClick={() => {
						lensOpen.value = false;
					}}
				>
					{LENS.close}
				</button>
			</div>
			{lensJob?.guardrails ? (
				<p class="dim" data-testid="lens-guardrails">
					{LENS.guardrailsOn}
				</p>
			) : null}
			{l && !lensEditable.value ? (
				<p class="dim" data-testid="lens-read-only">
					{VERSIONS.lensReadOnly}
				</p>
			) : null}
			{!l ? (
				<p class="dim">{LENS.noRun}</p>
			) : (
				<>
					<h3>{LENS.channelsTitle}</h3>
					<table class="lens-channels" data-testid="lens-channels">
						<thead>
							<tr>
								<th scope="col" />
								<th scope="col">{LENS.indexBefore}</th>
								<th scope="col">{l.lenient ? LENS.channelsWithout : LENS.indexAfter}</th>
								{l.lenient ? <th scope="col">{LENS.channelsWithLenient}</th> : null}
							</tr>
						</thead>
						<tbody>
							{l.channels
								.filter(
									(c) =>
										c.over ||
										c.overLenient === true ||
										c.notRewritten !== undefined ||
										(c.delta !== null && Math.abs(c.delta) >= 0.05) ||
										(c.deltaLenient !== null &&
											c.deltaLenient !== undefined &&
											Math.abs(c.deltaLenient) >= 0.05)
								)
								.map((c) => (
									<tr
										key={c.id}
										class={c.over || c.overLenient ? 'lens-over' : ''}
										data-channel={c.id}
									>
										<th scope="row" class="mono">
											{c.id}
										</th>
										<td>{c.before === null ? '–' : fmt1(c.before)}</td>
										<td>
											{c.after === null ? '–' : fmt1(c.after)}{' '}
											{c.delta !== null ? <span class="dim">({fmtSigned1(c.delta)})</span> : null}
											{c.over ? <span class="dim"> · {LENS.over}</span> : null}
											{c.notRewritten !== undefined ? (
												<span class="dim" data-testid={`lens-not-rewritten-${c.id}`}>
													{' '}
													· {LENS.notRewritten(c.notRewritten)}
												</span>
											) : null}
										</td>
										{l.lenient ? (
											<td data-testid={`lens-lenient-${c.id}`}>
												{c.withLenient === null || c.withLenient === undefined
													? '–'
													: fmt1(c.withLenient)}{' '}
												{c.deltaLenient !== null && c.deltaLenient !== undefined ? (
													<span class="dim">({fmtSigned1(c.deltaLenient)})</span>
												) : null}
												{c.overLenient ? <span class="dim"> · {LENS.over}</span> : null}
												{c.contributors && c.contributors.length > 0 ? (
													<span class="dim mono">
														{' '}
														{LENS.contributors(c.contributors.map((x) => x.unit).join(', '))}
													</span>
												) : null}
											</td>
										) : null}
									</tr>
								))}
						</tbody>
					</table>

					{l.reverted.length > 0 ? (
						<>
							<h3>{LENS.reverted}</h3>
							<ul data-testid="lens-reverted">
								{l.reverted.map((r) => (
									<li key={r.id}>{r.reason}</li>
								))}
							</ul>
						</>
					) : null}

					{l.declined.length > 0 ? (
						<>
							<h3>{LENS.declined}</h3>
							<ul data-testid="lens-declined">
								{l.declined.map((d) => (
									<li key={d.id}>
										{LENS.declinedItem(
											String((l.units.find((u) => u.id === d.id)?.para ?? -1) + 1),
											d.reason
										)}
									</li>
								))}
							</ul>
						</>
					) : null}

					<CoveragePanel l={l} />

					<h3>{LENS.chunksTitle}</h3>
					<ul class="dim lens-legend" data-testid="lens-legend">
						<li>
							<strong>{LENS.marks.accepted}</strong>: {BENCH_EXPLAIN.outcomes.accepted}
						</li>
						<li>
							<strong>{LENS.chunkPartial}</strong>: {BENCH_EXPLAIN.outcomes.partial}
						</li>
						<li>
							<strong>{LENS.chunkFailed}</strong>: {BENCH_EXPLAIN.outcomes.failed}
						</li>
						<li>
							<strong>
								<TermIn text={LENS.marks['kept-best']} word="kept best" k="kept-best" />
							</strong>
							: {BENCH_EXPLAIN.outcomes.keptBest}
						</li>
						<li>
							<strong>{LENS.marks.reverted}</strong>: {BENCH_EXPLAIN.outcomes.reverted}
						</li>
						{l.declined.length > 0 ? (
							<li>
								<strong>{LENS.marks.declined}</strong>: {BENCH_EXPLAIN.outcomes.declined}
							</li>
						) : null}
					</ul>
					<ul class="lens-chunks" data-testid="lens-chunks">
						{l.chunks.map((c) => (
							<li key={c.key} data-status={c.status}>
								<strong>{c.label}</strong>
								{c.messages.map((m) => (
									<p key={m} class="dim">
										<ChannelIdsIn text={m} />
									</p>
								))}
								{c.units.some(
									(id) => l.units.find((u) => u.id === id)?.keptBest && !gone.has(id)
								) ? (
									<button
										type="button"
										class="btn btn-secondary btn-sm"
										disabled={!lensEditable.value}
										onClick={() => revertChunk(c.key)}
									>
										{LENS.revertChunk}
									</button>
								) : null}
							</li>
						))}
					</ul>

					<p class="dim" data-testid="lens-bar">
						{LENS.bar(
							String(l.bar.barFailed),
							String(l.bar.keptLenient),
							String(l.bar.keptStrict),
							String(l.bar.attempted),
							`${(l.bar.upper95 * 100).toFixed(1)}%`
						)}
					</p>
					<h3>{LENS.unitsTitle}</h3>
					{l.severities.length > 0 ? (
						<div class="lens-severity" data-testid="lens-severity">
							<label for="lens-severity-filter">{LENS.severityFilter}</label>{' '}
							<select
								id="lens-severity-filter"
								data-testid="lens-severity-filter"
								value={filter}
								onChange={(e) => {
									severityFilter.value = (e.target as HTMLSelectElement).value as
										| 'all'
										| (typeof SEVERITIES)[number];
								}}
							>
								<option value="all">{LENS.severityAll}</option>
								{l.severities.map((s) => (
									<option key={s.severity} value={s.severity}>
										{LENS.severityCount(
											LENS.severities[s.severity],
											String(s.kept),
											String(s.failed)
										)}
									</option>
								))}
							</select>
							{l.severities
								.filter(
									(s) => (s.severity === 'style' || s.severity === 'signal-negative') && s.kept > 0
								)
								.map((s) => (
									<button
										key={s.severity}
										type="button"
										class="btn btn-secondary btn-sm"
										data-testid={`lens-revert-${s.severity}`}
										disabled={!lensEditable.value}
										onClick={() => revertBySeverity(s.severity)}
									>
										{LENS.revertSeverity(LENS.severities[s.severity])}
									</button>
								))}
						</div>
					) : null}
					{keptLenient.length > 0 ? (
						<button
							type="button"
							class="btn btn-secondary"
							data-testid="lens-revert-lenient"
							disabled={!lensEditable.value}
							onClick={revertAllLenient}
						>
							{LENS.revertLenient}
						</button>
					) : null}
					{kept.length > 0 ? (
						<button
							type="button"
							class="btn btn-secondary"
							data-testid="lens-revert-all"
							disabled={!lensEditable.value}
							onClick={revertAllKept}
						>
							{LENS.revertAll}
						</button>
					) : null}
					{keptLenient.length > 0 ? (
						<label class="lens-appendix">
							<input
								type="checkbox"
								data-testid="lens-export-appendix"
								checked={exportAppendix.value}
								onChange={(e) => {
									exportAppendix.value = (e.target as HTMLInputElement).checked;
								}}
							/>{' '}
							{LENS.exportAppendix}
						</label>
					) : null}
					<ul class="lens-units" data-testid="lens-units">
						{shown.map((u) => (
							<UnitRow key={u.id} u={u} reverted={gone.has(u.id)} />
						))}
					</ul>
				</>
			)}
		</aside>
	);
}
